import { Search, MapPin, Utensils, Star, Smartphone, CheckCircle2, ArrowRight, FileText, Navigation, Phone } from "lucide-react";
import { Link } from "wouter";
import { Button } from "@/components/ui/button";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import SEO from "@/components/SEO";

const WieFunktioniertSpeiseGuruPage = () => {
  // Proof/Trust Stats (können später aus API kommen)
  const proofStats = {
    menuCount: "5.000+",
    cityCount: "200+",
    updateFrequency: "Täglich",
    directFromRestaurant: "100%"
  };

  const steps = [
    {
      icon: <Search className="h-8 w-8" />,
      title: "Restaurant finden",
      description: "Finde Restaurants nach Küche, Preis und Highlights.",
      actionText: "Jetzt suchen",
      actionLink: "/restaurants",
      color: "from-primary to-primary-light",
      previewType: "restaurant-list"
    },
    {
      icon: <MapPin className="h-8 w-8" />,
      title: "Standort wählen",
      description: "Nutze deinen Standort für Treffer in deiner Nähe.",
      actionText: "Standort aktivieren",
      actionLink: "/restaurants",
      color: "from-primary to-primary-light",
      previewType: "location-map"
    },
    {
      icon: <Utensils className="h-8 w-8" />,
      title: "Speisekarte ansehen",
      description: "Sieh die echte Speisekarte – keine veralteten Infos.",
      actionText: "Beispiel ansehen",
      actionLink: "/restaurants",
      color: "from-orange-500 to-red-500",
      previewType: "menu-preview"
    },
    {
      icon: <Star className="h-8 w-8" />,
      title: "Entscheiden & genießen",
      description: "Anrufen, Route, Speisekarte – in unter 1 Minute bereit.",
      actionText: "Route starten",
      actionLink: "/restaurants",
      color: "from-purple-500 to-pink-500",
      previewType: "quick-actions"
    }
  ];

  const features = [
    {
      icon: <Smartphone className="h-6 w-6" />,
      title: "Überall schnell entscheiden",
      description: "Perfekt auf jedem Gerät – entscheide schnell, wo du essen möchtest.",
      proof: "Entscheide schneller"
    },
    {
      icon: <CheckCircle2 className="h-6 w-6" />,
      title: "Echte Speisekarten direkt vom Restaurant",
      description: "Immer aktuelle und originale Speisekarten – keine veralteten Informationen mehr.",
      proof: "Keine veralteten Menüs"
    },
    {
      icon: <MapPin className="h-6 w-6" />,
      title: "Standortbasiert & präzise",
      description: "Finde Restaurants in deiner Nähe mit präziser Standorterkennung.",
      proof: "Treffer in deiner Nähe"
    }
  ];

  const faqs = [
    {
      question: "Sind die Speisekarten aktuell?",
      answer: "Ja! Wir arbeiten direkt mit Restaurants zusammen und aktualisieren die Speisekarten regelmäßig. Alle Speisekarten kommen direkt vom Restaurant und werden täglich überprüft."
    },
    {
      question: "Kostet die Nutzung etwas?",
      answer: "Nein, SpeiseGURU ist für dich komplett kostenlos. Du kannst alle Funktionen ohne Einschränkungen nutzen – von der Suche bis zur Speisekartenansicht."
    },
    {
      question: "Woher kommen die Speisekarten?",
      answer: "Alle Speisekarten werden direkt von den Restaurants bereitgestellt. Wir sammeln keine veralteten Informationen von Drittanbietern, sondern arbeiten eng mit den Restaurants zusammen."
    },
    {
      question: "Kann ich ohne Standort suchen?",
      answer: "Ja, du kannst auch ohne Standortfreigabe suchen. Gib einfach eine Stadt oder Adresse manuell ein. Die Standortfreigabe hilft dir jedoch, Restaurants in deiner unmittelbaren Nähe zu finden."
    },
    {
      question: "Wie können Restaurants mitmachen?",
      answer: "Restaurants können ihre Speisekarte kostenlos über unser Formular einreichen. Wir prüfen die Angaben und veröffentlichen die Speisekarte dann auf unserer Plattform. Kontaktiere uns gerne für mehr Informationen."
    }
  ];

  // Mini-Preview Placeholder Component
  const PreviewPlaceholder = ({ type }: { type: string }) => {
    const previews = {
      "restaurant-list": (
        <div className="w-full h-24 bg-gradient-to-br from-primary/20 to-primary/5 rounded-lg border border-primary/20 flex items-center justify-center">
          <div className="flex gap-2">
            <div className="w-12 h-12 bg-white rounded shadow-sm"></div>
            <div className="w-12 h-12 bg-white rounded shadow-sm"></div>
            <div className="w-12 h-12 bg-white rounded shadow-sm"></div>
          </div>
        </div>
      ),
      "location-map": (
        <div className="w-full h-24 bg-gradient-to-br from-blue-50 to-blue-100 rounded-lg border border-blue-200 flex items-center justify-center relative">
          <MapPin className="h-8 w-8 text-primary absolute" />
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="w-16 h-16 border-2 border-primary/30 rounded-full"></div>
          </div>
        </div>
      ),
      "menu-preview": (
        <div className="w-full h-24 bg-gradient-to-br from-orange-50 to-orange-100 rounded-lg border border-orange-200 flex items-center justify-center">
          <FileText className="h-10 w-10 text-orange-500" />
        </div>
      ),
      "quick-actions": (
        <div className="w-full h-24 bg-gradient-to-br from-purple-50 to-purple-100 rounded-lg border border-purple-200 flex items-center justify-center gap-3">
          <Phone className="h-6 w-6 text-purple-500" />
          <Navigation className="h-6 w-6 text-purple-500" />
          <FileText className="h-6 w-6 text-purple-500" />
        </div>
      )
    };
    return previews[type as keyof typeof previews] || <div className="w-full h-24 bg-gray-100 rounded-lg"></div>;
  };

  return (
    <main className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-gray-50">
      <SEO 
        title="So funktioniert SpeiseGuru"
        description="Erfahre, wie du mit SpeiseGuru Restaurants findest, echte Speisekarten siehst und schnell entscheidest."
      />
      {/* Hero Section */}
      <section className="relative bg-gradient-to-r from-primary-dark via-primary to-primary-light text-white overflow-hidden">
        <div className="absolute inset-0 bg-black/10"></div>
        <div className="absolute inset-0" style={{
          backgroundImage: `radial-gradient(circle at 20% 50%, rgba(255,255,255,0.1) 0%, transparent 50%),
                            radial-gradient(circle at 80% 80%, rgba(255,255,255,0.1) 0%, transparent 50%)`
        }}></div>
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24 md:py-32">
          <div className="text-center animate-fade-in-up">
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-display font-bold mb-6 tracking-tight leading-[1.15] pb-2">
              Wie funktioniert <span className="text-white/90">SpeiseGURU</span>?
            </h1>
            <p className="text-xl md:text-2xl text-white/90 mb-8 max-w-3xl mx-auto leading-relaxed">
              Dein Weg zu den besten Restaurants – mit echten Speisekarten direkt vom Restaurant.
            </p>
            
            {/* CTA Button */}
            <div className="mb-6">
              <Link href="/restaurants">
                <Button
                  size="lg"
                  className="bg-white text-primary hover:bg-white/95 hover:shadow-2xl rounded-full px-8 py-6 text-lg font-semibold focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-offset-2 focus-visible:ring-offset-primary transition-colors duration-300"
                  aria-label="Jetzt Restaurants entdecken"
                >
                  Jetzt Restaurants entdecken
                  <ArrowRight className="ml-2 h-5 w-5" aria-hidden="true" />
                </Button>
              </Link>
            </div>

            {/* Proof Chips */}
            <div className="flex flex-wrap items-center justify-center gap-4 text-sm text-white/90">
              <span className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-white/10 backdrop-blur-sm rounded-full border border-white/20">
                <CheckCircle2 className="h-3.5 w-3.5" aria-hidden="true" />
                Echte Speisekarten
              </span>
              <span className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-white/10 backdrop-blur-sm rounded-full border border-white/20">
                <CheckCircle2 className="h-3.5 w-3.5" aria-hidden="true" />
                Schnell entscheiden
              </span>
              <span className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-white/10 backdrop-blur-sm rounded-full border border-white/20">
                <CheckCircle2 className="h-3.5 w-3.5" aria-hidden="true" />
                Standortbasiert
              </span>
            </div>
          </div>
        </div>
      </section>

      {/* Steps Section */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-20 pb-24 overflow-visible">
        <div className="text-center mb-16 animate-fade-in-up">
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
            So einfach geht's
          </h2>
          <p className="text-lg md:text-xl text-gray-600 max-w-2xl mx-auto leading-relaxed">
            In nur 4 einfachen Schritten zu deinem Lieblingsrestaurant
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 lg:gap-8 overflow-visible">
          {steps.map((step, index) => (
            <article
              key={index}
              className={`relative animate-fade-in-up overflow-visible ${index === 0 ? '' : `animation-delay-${Math.min(index * 100, 500)}`}`}
            >
              <div className="bg-white rounded-2xl p-6 shadow-lg hover:shadow-2xl transition-colors duration-300 h-full border border-gray-100 flex flex-col overflow-visible">
                {/* Step Number Label - links im Header */}
                <div className="flex items-center gap-3 mb-4">
                  <span className="inline-flex items-center px-2.5 py-1 bg-primary/10 text-primary text-xs font-semibold rounded-full border border-primary/20">
                    Schritt {index + 1}
                  </span>
                </div>
                
                {/* Icon */}
                <div className={`w-14 h-14 rounded-xl bg-gradient-to-br ${step.color} flex items-center justify-center text-white mb-4 shadow-lg`} aria-hidden="true">
                  {step.icon}
                </div>
                
                {/* Content */}
                <h3 className="text-xl font-bold text-gray-900 mb-2">
                  {step.title}
                </h3>
                <p className="text-gray-600 leading-relaxed mb-4 text-base flex-grow">
                  {step.description}
                </p>

                {/* Mini Preview */}
                <div className="mb-4">
                  <PreviewPlaceholder type={step.previewType} />
                </div>

                {/* Mini Action Link */}
                <Link href={step.actionLink}>
                  <span className="inline-flex items-center text-sm font-medium text-primary hover:text-primary-dark transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 rounded-md px-2 py-1 -ml-2">
                    {step.actionText}
                    <ArrowRight className="ml-1.5 h-3.5 w-3.5" aria-hidden="true" />
                  </span>
                </Link>
              </div>
            </article>
          ))}
        </div>
      </section>

      {/* Proof / Trust Section */}
      <section className="bg-white border-y border-gray-200 py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-6">
            <div className="text-center">
              <div className="text-3xl md:text-4xl font-bold text-primary mb-2">
                {proofStats.menuCount}
              </div>
              <div className="text-sm md:text-base text-gray-600">
                Speisekarten
              </div>
            </div>
            <div className="text-center">
              <div className="text-3xl md:text-4xl font-bold text-primary mb-2">
                {proofStats.cityCount}
              </div>
              <div className="text-sm md:text-base text-gray-600">
                Städte
              </div>
            </div>
            <div className="text-center">
              <div className="text-3xl md:text-4xl font-bold text-primary mb-2">
                {proofStats.updateFrequency}
              </div>
              <div className="text-sm md:text-base text-gray-600">
                Updates
              </div>
            </div>
            <div className="text-center">
              <div className="text-3xl md:text-4xl font-bold text-primary mb-2">
                {proofStats.directFromRestaurant}
              </div>
              <div className="text-sm md:text-base text-gray-600">
                Direkt vom Restaurant
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features / Why Section */}
      <section className="bg-gradient-to-r from-gray-900 via-gray-800 to-gray-900 text-white py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16 animate-fade-in-up">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">
              Warum SpeiseGURU?
            </h2>
            <p className="text-lg md:text-xl text-gray-100 max-w-2xl mx-auto leading-relaxed">
              Die einzigartigen Vorteile, die deine Restaurant-Suche einfacher machen
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 lg:gap-8">
            {features.map((feature, index) => (
              <article
                key={index}
                className={`bg-gray-800/70 backdrop-blur-sm rounded-2xl p-8 border-2 border-gray-700 hover:border-primary hover:shadow-xl hover:shadow-primary/30 transition-colors duration-300 animate-fade-in-up ${index === 0 ? '' : `animation-delay-${Math.min(index * 100, 500)}`} focus-within:ring-2 focus-within:ring-primary focus-within:ring-offset-2 focus-within:ring-offset-gray-800`}
                tabIndex={0}
              >
                <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-primary to-primary-dark flex items-center justify-center text-white mb-6 shadow-lg" aria-hidden="true">
                  {feature.icon}
                </div>
                <h3 className="text-xl font-bold mb-3 text-white">
                  {feature.title}
                </h3>
                <p className="text-gray-100 leading-relaxed text-base mb-3">
                  {feature.description}
                </p>
                {/* Micro-Proof-Line */}
                <div className="flex items-center gap-2 text-sm text-primary/90 font-medium">
                  <CheckCircle2 className="h-4 w-4" aria-hidden="true" />
                  <span>{feature.proof}</span>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
        <div className="text-center mb-12">
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
            Häufige Fragen
          </h2>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto leading-relaxed">
            Alles, was du über SpeiseGURU wissen musst
          </p>
        </div>

        <Accordion type="single" collapsible className="w-full space-y-3">
          {faqs.map((faq, index) => (
            <AccordionItem
              key={index}
              value={`item-${index}`}
              className="bg-white rounded-lg border border-gray-200 px-6 shadow-sm hover:shadow-md hover:border-primary/30 transition-colors duration-200"
            >
              <AccordionTrigger className="text-left font-semibold text-gray-900 hover:no-underline py-6 hover:text-primary transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 rounded-md">
                {faq.question}
              </AccordionTrigger>
              <AccordionContent className="text-gray-600 leading-relaxed pb-6 text-base">
                {faq.answer}
              </AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>

        {/* Contact Link */}
        <div className="mt-8 text-center">
          <p className="text-gray-600 mb-2">
            Noch Fragen?
          </p>
          <Link href="/kontakt">
            <span className="inline-flex items-center text-primary hover:text-primary-dark font-medium text-base focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 rounded-md px-2 py-1">
              Kontaktiere uns
              <ArrowRight className="ml-1.5 h-4 w-4" aria-hidden="true" />
            </span>
          </Link>
        </div>
      </section>

      {/* Final CTA Section */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
        <div className="bg-gradient-to-r from-primary-dark to-primary rounded-3xl p-12 md:p-16 text-center text-white shadow-2xl animate-scale-in">
          <h2 className="text-3xl md:text-4xl font-bold mb-6">
            Bereit, dein nächstes Restaurant zu entdecken?
          </h2>
          <p className="text-lg md:text-xl text-white/90 mb-8 max-w-2xl mx-auto leading-relaxed">
            Starte jetzt – mit echten Speisekarten und schnellen Entscheidungen.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-4">
            <Link href="/restaurants">
              <Button
                size="lg"
                className="bg-white text-primary hover:bg-white/95 hover:shadow-2xl rounded-full px-8 py-6 text-lg font-semibold focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-offset-2 focus-visible:ring-offset-primary transition-colors duration-300"
                aria-label="Restaurants entdecken"
              >
                Restaurants entdecken
                <ArrowRight className="ml-2 h-5 w-5" aria-hidden="true" />
              </Button>
            </Link>
            <Link href="/kontakt">
              <Button
                variant="ghost"
                size="lg"
                className="text-white hover:bg-white/10 hover:text-white rounded-full px-6 py-6 text-base font-medium focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-offset-2 focus-visible:ring-offset-primary transition-colors duration-300 border border-white/20"
                aria-label="Für Restaurants: Speisekarte einreichen"
              >
                Für Restaurants: Speisekarte einreichen
              </Button>
            </Link>
          </div>
          {/* Microcopy */}
          <p className="text-sm text-white/80">
            Kostenlos • Schnell • Echte Speisekarten
          </p>
        </div>
      </section>
    </main>
  );
};

export default WieFunktioniertSpeiseGuruPage;
